import { TBaseButtonChildrenProps } from '../base-button/BaseButton.types';
import { forwardRef } from 'react';
import { TextIconButtonProps } from './TextIconButton.types';
import Button from '../button/Button';
import { ButtonIconChild } from '../base-button-children/button-icon-child/ButtonIconChild';
import { TEXT_ICON_BUTTON_SIZES, TEXT_ICON_BUTTON_TYPES } from './TextIconButton.constants';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { BUTTON_TEXT_POSITIONS } from '../button/Button.types.constants';
import { styles } from './TextIconButton.styles';
import { withPersonalizedTextIconButtonStyle } from './withPersonalizedTextIconButtonStyle';

const TEXT_ICON_BUTTON_SIZE_STYLES_MAP = {
  [TEXT_ICON_BUTTON_SIZES.SMALL]: { button: styles.buttonSmall, icon: styles.iconSmall, text: styles.textSmall },
  [TEXT_ICON_BUTTON_SIZES.MEDIUM]: { button: styles.buttonMedium, icon: styles.iconMedium, text: styles.textMedium },
  [TEXT_ICON_BUTTON_SIZES.LARGE]: { button: styles.buttonLarge, icon: styles.iconLarge, text: styles.textLarge },
};

const TEXT_ICON_BUTTON_TYPE_STYLES_MAP = {
  [TEXT_ICON_BUTTON_TYPES.ICON_RIGHT]: {
    button: styles.buttonIconRight,
    icon: styles.iconIconRight,
    text: styles.textIconRight,
  },
  [TEXT_ICON_BUTTON_TYPES.ICON_LEFT]: {
    button: styles.buttonIconLeft,
    icon: styles.iconIconLeft,
    text: styles.textIconLeft,
  },
  [TEXT_ICON_BUTTON_TYPES.NO_ICON]: { button: styles.buttonNoIcon, icon: styles.iconNoIcon, text: styles.textNoIcon },
  [TEXT_ICON_BUTTON_TYPES.ONLY_CTA]: {
    button: styles.buttonOnlyCta,
    icon: styles.iconOnlyCta,
    text: styles.textOnlyCta,
  },
};

const TextIconButtonComponent = forwardRef(
  (
    {
      size,
      type,
      iconName,
      iconDisabledStyle,
      iconFocusStyle,
      iconHoverStyle,
      iconPressedStyle,
      iconTestID,
      style,
      disabledStyle,
      textStyle,
      textDisabledStyle,
      iconStyle,
      ...buttonProps
    }: TextIconButtonProps,
    ref,
  ) => {
    const mergedButtonStyle = mergeStyles(
      TEXT_ICON_BUTTON_SIZE_STYLES_MAP[size]?.button,
      TEXT_ICON_BUTTON_TYPE_STYLES_MAP[type]?.button,
      style,
    );
    const mergedTextStyle = mergeStyles(
      TEXT_ICON_BUTTON_SIZE_STYLES_MAP[size]?.text,
      TEXT_ICON_BUTTON_TYPE_STYLES_MAP[type]?.text,
      textStyle,
    );
    const mergedIconStyle = mergeStyles(
      TEXT_ICON_BUTTON_SIZE_STYLES_MAP[size]?.icon,
      TEXT_ICON_BUTTON_TYPE_STYLES_MAP[type]?.icon,
      iconStyle,
    );
    const textPosition =
      TEXT_ICON_BUTTON_TYPES.ICON_LEFT === type ? BUTTON_TEXT_POSITIONS.RIGHT : BUTTON_TEXT_POSITIONS.LEFT;

    return (
      <Button
        ref={ref}
        style={mergedButtonStyle}
        disabledStyle={disabledStyle}
        textStyle={mergedTextStyle}
        textDisabledStyle={textDisabledStyle}
        textPosition={textPosition}
        {...buttonProps}
      >
        {(baseButtonChildrenProps: TBaseButtonChildrenProps) => {
          return TEXT_ICON_BUTTON_TYPES.NO_ICON !== type && TEXT_ICON_BUTTON_TYPES.ONLY_CTA !== type ? (
            <ButtonIconChild
              {...{
                iconName,
                iconStyle: mergedIconStyle,
                iconDisabledStyle,
                iconFocusStyle,
                iconHoverStyle,
                iconPressedStyle,
                iconTestID,
                ...baseButtonChildrenProps,
              }}
            />
          ) : null;
        }}
      </Button>
    );
  },
);

TextIconButtonComponent.displayName = 'TextIconButton';

export const TextIconButton = withPersonalizedTextIconButtonStyle(TextIconButtonComponent, styles);
